'use client';

import { c as _c } from "react/compiler-runtime";
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { Button, Checkbox, DropdownMenu, Input, InputDecorator, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { FileUploadPlusBox } from '@/app/_components/FileUploadPlusBox';
import { INQUIRY_TYPE } from '@/app/donation/constants';
import type { InquiryType } from '@/app/donation/types';
import { Image } from '@/components/client';
import type { InquiryFormParams } from './_hooks/useInquiryForm';
import { useInquiryForm } from './_hooks/useInquiryForm';
import { INQUIRY_DETAIL_TYPE, INQUIRY_DETAIL_TYPE_OPTIONS_MAP } from './constants';
import type { InquiryDetailType } from './types';
interface InquiryFormProps {
  type: InquiryType;
  isEdit?: boolean;
  orderId?: string;
  productNumber?: string;
  loading?: boolean;
  onSubmit?: (data: InquiryFormParams) => void;
  /** 기본 값 */
  fileUrlList?: string[];
  title?: string;
  contents?: string;
  privateYN?: boolean;
}
export const InquiryForm = t0 => {
  const $ = _c(95);
  const {
    type,
    isEdit,
    orderId,
    productNumber,
    loading,
    fileUrlList: defaultFileUrlList,
    title: defaultTitle,
    contents: defaultContents,
    privateYN: defaultPrivateYN,
    onSubmit
  } = t0;
  const {
    toast
  } = useToast();
  const {
    form
  } = useInquiryForm();
  const [inquiryDetailType, setInquiryDetailType] = useState();
  const {
    watch,
    setValue,
    register,
    trigger,
    reset,
    formState: t1
  } = form;
  const {
    errors
  } = t1;
  const fileUrlList = watch("fileUrlList");
  let t2;
  if ($[0] !== watch) {
    t2 = watch("privateYN");
    $[0] = watch;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const privateYN = t2;
  let t3;
  let t4;
  if ($[2] !== defaultContents || $[3] !== defaultFileUrlList || $[4] !== defaultPrivateYN || $[5] !== defaultTitle || $[6] !== reset) {
    t3 = () => {
      reset({
        fileUrlList: defaultFileUrlList ?? [],
        title: defaultTitle ?? "",
        contents: defaultContents ?? "",
        privateYN: Boolean(defaultPrivateYN)
      });
    };
    t4 = [defaultFileUrlList, defaultTitle, defaultContents, defaultPrivateYN, reset];
    $[2] = defaultContents;
    $[3] = defaultFileUrlList;
    $[4] = defaultPrivateYN;
    $[5] = defaultTitle;
    $[6] = reset;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== productNumber) {
    t5 = UnauthQueryOptions["get:/wello/v2/allows/home/gift-product/{productNumber}"]({
      path: {
        productNumber
      }
    });
    $[9] = productNumber;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = Boolean(productNumber) && type === INQUIRY_TYPE.GIFT_PRODUCT;
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = {
      ...t5,
      enabled: t6,
      select: _temp
    };
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    data: giftProductInfo,
    isLoading: isLoadingGiftProductInfo
  } = useQuery(t7);
  let t8;
  if ($[14] !== orderId) {
    t8 = AuthQueryOptions["get:/wello/v2/auth/home/gift/order/{orderId}"]({
      path: {
        orderId
      }
    });
    $[14] = orderId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = Boolean(orderId) && type === INQUIRY_TYPE.DELIVERY;
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = {
      ...t8,
      enabled: t9,
      select: _temp2
    };
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    data: orderInfo,
    isLoading: isLoadingOrderInfo
  } = useQuery(t10);
  const isShowOrderIdArea = type === INQUIRY_TYPE.DELIVERY;
  const isShowProductArea = type === INQUIRY_TYPE.GIFT_PRODUCT || type === INQUIRY_TYPE.DELIVERY;
  const isShowFileUploadArea = type === INQUIRY_TYPE.ETC || type === INQUIRY_TYPE.DELIVERY;
  const isShowPrivateArea = type === INQUIRY_TYPE.GIFT_PRODUCT;
  const isFormLoading = loading || isLoadingGiftProductInfo || isLoadingOrderInfo;
  let t11;
  if ($[19] !== toast) {
    t11 = detailType => {
      switch (detailType) {
        case INQUIRY_DETAIL_TYPE.PRODUCT_INFO:
          {
            return true;
          }
        default:
          {
            toast({
              message: "\uD574\uB2F9 \uC720\uD615\uC740 \uCC44\uB110\uD1A1\uC73C\uB85C \uBB38\uC758\uD574\uC8FC\uC138\uC694.",
              type: "warning"
            });
            ChannelService.showMessenger();
            return false;
          }
      }
    };
    $[19] = toast;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const validationDetailType = t11;
  let t12;
  if ($[21] !== isFormLoading) {
    t12 = cn("relative flex h-full flex-1 flex-col", "mobile-tablet:pt-20", {
      "blur-sm": isFormLoading
    });
    $[21] = isFormLoading;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isShowOrderIdArea || $[24] !== orderId) {
    t13 = isShowOrderIdArea && <div className={cn("mb-20 flex items-center gap-8 rounded-xsmall bg-Gray-50 px-20 py-12", "mobile-tablet:rounded-none mobile-tablet:px-20 mobile-tablet:py-16")}><p className="font-400 text-Gray-600 typo-body4">주문번호</p><p className="font-500 text-Gray-800 typo-body4">{orderId}</p></div>;
    $[23] = isShowOrderIdArea;
    $[24] = orderId;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== giftProductInfo?.donationAmount || $[27] !== giftProductInfo?.institutionName || $[28] !== giftProductInfo?.logoUrl || $[29] !== giftProductInfo?.name || $[30] !== giftProductInfo?.thumbnailUrl || $[31] !== isShowProductArea || $[32] !== orderInfo?.institutionName || $[33] !== orderInfo?.logoUrl || $[34] !== orderInfo?.name || $[35] !== orderInfo?.option || $[36] !== orderInfo?.thumbnailUrl || $[37] !== type) {
    t14 = isShowProductArea && <section className={cn("flex items-center gap-12 border-Gray-100 px-16", "mobile-tablet:border-b-8 mobile-tablet:px-20", type === INQUIRY_TYPE.GIFT_PRODUCT ? "pb-20" : "pb-35")}><div className="relative shrink-0"><Image src={type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.thumbnailUrl : orderInfo?.thumbnailUrl} alt={"\uC0C1\uD488 \uC774\uBBF8\uC9C0"} className="aspect-[1/1] w-full rounded-large border border-Gray-black/5 object-cover" width={80} height={80} /></div><div className="flex flex-col"><div className="flex items-center gap-2 pb-2"><div className="relative shrink-0 overflow-hidden rounded-pill border border-Gray-300"><Image src={type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.logoUrl : orderInfo?.logoUrl} alt={"\uC9C0\uC790\uCCB4 \uB85C\uACE0 \uC774\uBBF8\uC9C0"} className="aspect-[1/1] w-full rounded-large object-cover" width={20} height={20} /></div><p className="line-clamp-1 font-600 text-Gray-600 typo-caption1">{type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.institutionName : orderInfo?.institutionName}</p></div><div className="flex flex-1 flex-col"><p className="line-clamp-1 font-500 text-Gray-900 typo-body4">{type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.name : orderInfo?.name}</p>{type === INQUIRY_TYPE.GIFT_PRODUCT ? <p className="line-clamp-1 pt-12 font-700 text-Gray-black typo-body2">{giftProductInfo?.donationAmount.toLocaleString()}원</p> : <p className="line-clamp-1 pt-6 font-400 text-Gray-600 typo-caption1">옵션: {orderInfo?.option ?? "-"}</p>}</div></div></section>;
    $[26] = giftProductInfo?.donationAmount;
    $[27] = giftProductInfo?.institutionName;
    $[28] = giftProductInfo?.logoUrl;
    $[29] = giftProductInfo?.name;
    $[30] = giftProductInfo?.thumbnailUrl;
    $[31] = isShowProductArea;
    $[32] = orderInfo?.institutionName;
    $[33] = orderInfo?.logoUrl;
    $[34] = orderInfo?.name;
    $[35] = orderInfo?.option;
    $[36] = orderInfo?.thumbnailUrl;
    $[37] = type;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("flex flex-col px-16 pb-20", "mobile-tablet:p-20");
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = isShowFileUploadArea && <section className="flex flex-col pb-32"><p className="font-400 text-Gray-800 typo-body4">첨부파일</p><ul className="flex w-full items-center gap-8 overflow-x-auto pb-16 pt-8 hide-scrollbar">{fileUrlList?.slice(0, FILE_UPLOAD_LIMIT_COUNT).map((fileUrl, index) => <li key={fileUrl}><FileUploadPlusBox type="HOME_INQUIRY" imageUrl={fileUrl} previewBlank={true} size={FILE_UPLOAD_BOX_SIZE} onDelete={() => {
          const newFileUrlList = fileUrlList?.filter((_, i) => i !== index);
          setValue("fileUrlList", newFileUrlList);
        }} /></li>)}{Array.from({
        length: FILE_UPLOAD_LIMIT_COUNT - fileUrlList.length
      }).map((__0, index_0) => <li key={index_0}><FileUploadPlusBox type="HOME_INQUIRY" size={FILE_UPLOAD_BOX_SIZE} limitByte={31457280} onUpload={fileUrl_0 => {
          if (!fileUrl_0) {
            toast({
              message: "\uCCA8\uBD80\uD30C\uC77C \uC5C5\uB85C\uB4DC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
              type: "fail"
            });
            return;
          }
          const newFileUrlList_0 = [...fileUrlList, fileUrl_0];
          setValue("fileUrlList", newFileUrlList_0);
        }} /></li>)}</ul><ul className="flex list-inside flex-col pl-20 font-400 text-Gray-600 typo-caption1"><li className="list-disc">파일 형식은 이미지 파일(jpg, jpeg, gif, png)로 제출 가능합니다.</li><li className="list-disc">파일 용량은 최대 30MB까지 가능합니다.</li></ul></section>;
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="font-400 text-Gray-800 typo-body4">문의 작성</p>;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== isShowPrivateArea || $[42] !== privateYN || $[43] !== setValue) {
    t18 = isShowPrivateArea && <Checkbox checked={privateYN} onCheckedChange={checked => {
      setValue("privateYN", checked);
    }}>비공개</Checkbox>;
    $[41] = isShowPrivateArea;
    $[42] = privateYN;
    $[43] = setValue;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t18) {
    t19 = <div className="flex items-center justify-between pb-8">{t17}{t18}</div>;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== inquiryDetailType || $[48] !== type || $[49] !== validationDetailType) {
    t20 = INQUIRY_DETAIL_TYPE_OPTIONS_MAP[type]?.length > 0 ? <DropdownMenu options={INQUIRY_DETAIL_TYPE_OPTIONS_MAP[type] ?? []} value={inquiryDetailType} onChange={value => {
      setInquiryDetailType(value);
      validationDetailType(value);
    }} placeholder={"\uBB38\uC758 \uC138\uBD80 \uC720\uD615\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."} className="mb-8" /> : null;
    $[47] = inquiryDetailType;
    $[48] = type;
    $[49] = validationDetailType;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = errors.title?.message;
  let t22;
  if ($[51] !== register) {
    t22 = register("title");
    $[51] = register;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22) {
    t23 = <Input placeholder={"\uBB38\uC758 \uC81C\uBAA9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} className="placeholder:font-400" dimensions="H48" {...t22} />;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t21 || $[56] !== t23) {
    t24 = <InputDecorator validationMessage={t21} className="mb-8">{t23}</InputDecorator>;
    $[55] = t21;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  const t25 = errors.contents?.message;
  let t26;
  if ($[58] !== register) {
    t26 = register("contents");
    $[58] = register;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t26) {
    t27 = <textarea className="h-240 resize-none rounded-xsmall border border-Gray-300 px-16 py-12 font-400 text-Gray-900 outline-none typo-body3 placeholder:font-400 placeholder:text-Gray-550 placeholder:typo-body3" placeholder={"\uBB38\uC758 \uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} {...t26} />;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t25 || $[63] !== t27) {
    t28 = <InputDecorator validationMessage={t25}>{t27}</InputDecorator>;
    $[62] = t25;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t19 || $[66] !== t20 || $[67] !== t24 || $[68] !== t28) {
    t29 = <section className="flex flex-col">{t19}{t20}{t24}{t28}</section>;
    $[65] = t19;
    $[66] = t20;
    $[67] = t24;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t15 || $[71] !== t16 || $[72] !== t29) {
    t30 = <section className={t15}>{t16}{t29}</section>;
    $[70] = t15;
    $[71] = t16;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cn("w-full bg-Gray-white px-16 py-12", "mobile-tablet:sticky mobile-tablet:bottom-0 mobile-tablet:mt-auto mobile-tablet:px-20 mobile-tablet:py-12 mobile-tablet:shadow-lg");
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== form || $[76] !== inquiryDetailType || $[77] !== isFormLoading || $[78] !== onSubmit || $[79] !== trigger || $[80] !== validationDetailType) {
    t32 = async () => {
      if (isFormLoading) {
        return;
      }
      if (!validationDetailType(inquiryDetailType)) {
        return;
      }
      const valid = await trigger();
      if (!valid) {
        return;
      }
      onSubmit?.(form.getValues());
    };
    $[75] = form;
    $[76] = inquiryDetailType;
    $[77] = isFormLoading;
    $[78] = onSubmit;
    $[79] = trigger;
    $[80] = validationDetailType;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  const t33 = isEdit ? "\uC218\uC815" : "\uB4F1\uB85D";
  let t34;
  if ($[82] !== isFormLoading || $[83] !== t32 || $[84] !== t33) {
    t34 = <footer className={t31}><Button theme="primary-fill" dimensions="H56" className="w-full" loading={isFormLoading} disabled={isFormLoading} onClick={t32}>{t33}</Button></footer>;
    $[82] = isFormLoading;
    $[83] = t32;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== isFormLoading) {
    t35 = isFormLoading ? <div className="absolute left-0 top-0 size-full bg-Gray-white/20 flex-center"><Spinner /></div> : <></>;
    $[86] = isFormLoading;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t12 || $[89] !== t13 || $[90] !== t14 || $[91] !== t30 || $[92] !== t34 || $[93] !== t35) {
    t36 = <section className={t12}>{t13}{t14}{t30}{t34}{t35}</section>;
    $[88] = t12;
    $[89] = t13;
    $[90] = t14;
    $[91] = t30;
    $[92] = t34;
    $[93] = t35;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  return t36;
};
const FILE_UPLOAD_LIMIT_COUNT = 5;
const FILE_UPLOAD_BOX_SIZE = 72;
function _temp(data) {
  return {
    logoUrl: data.context?.institutionLogoUrl,
    thumbnailUrl: data.context?.mainImageUrl,
    institutionName: data.context?.institutionName,
    name: data.context?.productName,
    donationAmount: data.context?.requiredDonationAmount ?? 0
  };
}
function _temp2(data_0) {
  const firstProduct = data_0.context?.products?.[0];
  return {
    logoUrl: data_0.context?.homeInstitutionLogoUrl,
    thumbnailUrl: firstProduct?.productMainImageUrl,
    institutionName: data_0.context?.homeInstitutionName,
    name: firstProduct?.productName,
    option: firstProduct?.optionItemList?.join(", ")
  };
}